import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { Panel, TactLoader } from "../components/Tactical";
import SocialCard from "../components/SocialCard";
import { toast } from "sonner";
import { Search, Users, UserPlus, Heart, Sparkles, Smartphone, Gamepad2 } from "lucide-react";
import DMChatDrawer from "../components/DMChatDrawer";

const TOP_TABS = [
  { id: "browse", labelKey: "social.tab.browse", testId: "social-tab-browse" },
  { id: "mine", labelKey: "social.tab.mine", testId: "social-tab-mine" },
];

const MINE_TABS = [
  { id: "friends", labelKey: "social.tab.friends", Icon: Sparkles, testId: "social-mine-friends" },
  { id: "following", labelKey: "social.tab.following", Icon: UserPlus, testId: "social-mine-following" },
  { id: "followers", labelKey: "social.tab.followers", Icon: Heart, testId: "social-mine-followers" },
];

const PLATFORMS = [
  { id: "all", labelKey: "social.platform.all", Icon: Users },
  { id: "mobile", labelKey: "social.platform.mobile", Icon: Smartphone },
  { id: "console", labelKey: "social.platform.console", Icon: Gamepad2 },
];

const SORTS = [
  { id: "points", labelKey: "social.sort.points" },
  { id: "reliability", labelKey: "social.sort.reliability" },
  { id: "winrate", labelKey: "social.sort.winrate" },
];

export default function Social() {
  const { user } = useAuth();
  const { t } = useT();
  const [params, setParams] = useSearchParams();
  const tab = TOP_TABS.some((tt) => tt.id === params.get("tab")) ? params.get("tab") : "browse";
  const setTab = (tt) => setParams({ tab: tt }, { replace: true });

  return (
    <div className="space-y-6" data-testid="social-page">
      {/* FUT-style stadium hero */}
      <section className="relative overflow-hidden border border-[#E4E4E7] dark:border-[#2A2A30]" data-testid="social-hero">
        <div
          className="absolute inset-0 bg-cover bg-center scale-110 blur-[3px]"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1522778119026-d647f0596c20?auto=format&fit=crop&w=1920&q=75')",
          }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/65 to-black/90" aria-hidden />
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.09]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, rgba(0,255,135,0.35) 0, rgba(0,255,135,0.35) 2px, transparent 2px, transparent 80px)",
          }}
          aria-hidden
        />
        <div className="relative p-6 sm:p-10 text-white">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FF87] mb-2">{t("social.heroKicker")}</div>
          <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-wide leading-none">
            {t("social.heroLine1")}<br />
            <span className="text-[#00FF87] drop-shadow-[0_0_18px_rgba(0,255,135,0.4)]">{t("social.heroLine2")}</span>
          </h1>
          <p className="mt-4 max-w-xl text-white/70 font-body">
            {t("social.heroIntro")}
          </p>
          <div className="mt-5 inline-flex gap-1 border border-white/20 p-1 bg-black/40 backdrop-blur" data-testid="social-top-tabs">
            {TOP_TABS.map((tt) => {
              const active = tab === tt.id;
              return (
                <button
                  key={tt.id}
                  onClick={() => setTab(tt.id)}
                  data-testid={tt.testId}
                  className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] transition-colors ${
                    active ? "bg-[#00FF87] text-black" : "text-white/80 hover:text-white"
                  }`}
                >
                  {t(tt.labelKey)}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {tab === "browse" ? <BrowseView viewer={user} /> : <MineView viewer={user} />}
    </div>
  );
}

function BrowseView({ viewer }) {
  const { t } = useT();
  const [platform, setPlatform] = useState("all");
  const [leagueId, setLeagueId] = useState("all");
  const [sort, setSort] = useState("points");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leagues, setLeagues] = useState([]);

  // Load active leagues for the dropdown (narrowed by platform)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/social/leagues-filter");
        setLeagues(data.rows || []);
      } catch { /* noop */ }
    })();
  }, []);

  const narrowedLeagues = useMemo(() => {
    if (platform === "all") return leagues;
    return leagues.filter((l) => (l.platform || "mobile") === platform);
  }, [leagues, platform]);

  // Reset league filter when platform changes & current league doesn't belong
  useEffect(() => {
    if (leagueId !== "all" && !narrowedLeagues.some((l) => l.id === leagueId)) {
      setLeagueId("all");
    }
  }, [platform, narrowedLeagues, leagueId]);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        platform, league_id: leagueId, sort,
        ...(q.trim() ? { q: q.trim() } : {}),
        limit: "60",
      }).toString();
      const { data } = await api.get(`/social/browse?${qs}`);
      setRows(data.rows || []);
    } catch (e) {
      toast.error(`Browse failed: ${formatApiError(e)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [platform, leagueId, sort]);

  // Debounce search
  useEffect(() => {
    const h = setTimeout(load, 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line
  }, [q]);

  const toggleFollow = async (p) => {
    if (!viewer || viewer === false) {
      toast.error(t("social.signinToFollow"));
      return;
    }
    const was = p.is_following;
    // optimistic UI
    setRows((prev) => prev.map((r) => r.id === p.id ? {
      ...r,
      is_following: !was,
      is_friend: !was && r.is_followed_by ? true : (was ? false : r.is_friend),
      followers_count: r.followers_count + (was ? -1 : 1),
    } : r));
    try {
      if (was) await api.delete(`/users/${p.id}/follow`);
      else await api.post(`/users/${p.id}/follow`);
    } catch (e) {
      toast.error(formatApiError(e));
      // revert
      setRows((prev) => prev.map((r) => r.id === p.id ? p : r));
    }
  };

  return (
    <div className="space-y-5" data-testid="social-browse">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3" data-testid="social-filter-bar">
        <div className="flex gap-1 border border-[#E4E4E7] dark:border-[#2A2A30] p-1">
          {PLATFORMS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPlatform(p.id)}
              data-testid={`social-platform-${p.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${
                platform === p.id ? "bg-[#7C3AED] text-white" : "text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
              }`}
            >
              <p.Icon className="w-3 h-3" /> {t(p.labelKey)}
            </button>
          ))}
        </div>
        <select
          value={leagueId}
          onChange={(e) => setLeagueId(e.target.value)}
          data-testid="social-league-select"
          className="bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#09090B] dark:text-[#F4F4F5] focus:outline-none focus:border-[#7C3AED]"
        >
          <option value="all">{t("social.allLeagues")}</option>
          {narrowedLeagues.map((l) => (
            <option key={l.id} value={l.id}>{(l.name || "UNNAMED").toUpperCase()} · {(l.platform || "").toUpperCase()} · {(l.status || "").toUpperCase()}</option>
          ))}
        </select>
        <div className="flex gap-1 border border-[#E4E4E7] dark:border-[#2A2A30] p-1" data-testid="social-sort-group">
          {SORTS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSort(s.id)}
              data-testid={`social-sort-${s.id}`}
              className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${
                sort === s.id ? "bg-[#00FF87] text-black" : "text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
              }`}
            >
              {t(s.labelKey)}
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#71717A]" aria-hidden />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="social-search-input"
            placeholder={t("social.searchPh")}
            className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] pl-9 pr-3 py-2 font-mono text-xs text-[#09090B] dark:text-[#F4F4F5] focus:outline-none focus:border-[#7C3AED]"
          />
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]" data-testid="social-browse-count">
          {rows.length === 1 ? t("social.player", { n: rows.length }) : t("social.players", { n: rows.length })}
        </span>
      </div>

      {loading ? (
        <TactLoader label={t("social.loadingShort")} />
      ) : rows.length === 0 ? (
        <Panel testId="social-browse-empty" className="font-mono text-sm text-[#71717A]">
          {t("social.noPlayersFilter")}
        </Panel>
      ) : (
        <div
          className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
          data-testid="social-browse-grid"
        >
          {rows.map((p) => (
            <SocialCard
              key={p.id}
              p={p}
              onFollow={toggleFollow}
              viewer={viewer}
              onRemoved={(uid) => setRows((prev) => prev.filter((r) => r.id !== uid))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MineView({ viewer }) {
  const { t } = useT();
  const [mineTab, setMineTab] = useState("friends");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatPeer, setChatPeer] = useState(null);

  const load = async () => {
    if (!viewer || viewer === false) return;
    setLoading(true);
    try {
      const path = mineTab === "friends"
        ? `/users/me/friends`
        : mineTab === "following"
          ? `/users/${viewer.id}/following`
          : `/users/${viewer.id}/followers`;
      const { data } = await api.get(path);
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mineTab, viewer]);

  if (!viewer || viewer === false) {
    return (
      <Panel testId="social-mine-anon" className="font-mono text-sm text-[#71717A]">
        [ <Link to="/login" className="text-[#7C3AED] hover:underline">{t("social.mineSignIn")}</Link> {t("social.mineAnon").replace(/^\[\s*/, "").replace(/\s*\]$/, "")} ]
      </Panel>
    );
  }

  const toggleFollow = async (p) => {
    const was = p.is_following;
    setRows((prev) => prev.map((r) => r.id === p.id ? { ...r, is_following: !was, is_friend: !was && r.is_followed_by ? true : (was ? false : r.is_friend) } : r));
    try {
      if (was) await api.delete(`/users/${p.id}/follow`);
      else await api.post(`/users/${p.id}/follow`);
      // If we just unfollowed someone, remove from "following" & "friends" tabs
      if (was && (mineTab === "following" || mineTab === "friends")) {
        setRows((prev) => prev.filter((r) => r.id !== p.id));
      }
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  return (
    <div className="space-y-5" data-testid="social-mine">
      <div className="flex gap-1 border border-[#E4E4E7] dark:border-[#2A2A30] p-1 inline-flex">
        {MINE_TABS.map((tt) => {
          const active = mineTab === tt.id;
          return (
            <button
              key={tt.id}
              onClick={() => setMineTab(tt.id)}
              data-testid={tt.testId}
              className={`inline-flex items-center gap-2 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${
                active ? "bg-[#7C3AED] text-white" : "text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
              }`}
            >
              <tt.Icon className="w-3 h-3" /> {t(tt.labelKey)}
            </button>
          );
        })}
      </div>

      {loading ? (
        <TactLoader label={t("social.loadingShort")} />
      ) : rows.length === 0 ? (
        <Panel testId={`social-mine-empty-${mineTab}`} className="font-mono text-sm text-[#71717A]">
          {mineTab === "friends" && t("social.empty.friends")}
          {mineTab === "following" && t("social.empty.following")}
          {mineTab === "followers" && t("social.empty.followers")}
        </Panel>
      ) : (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4" data-testid={`social-mine-grid-${mineTab}`}>
          {rows.map((p) => (
            <SocialCard
              key={p.id}
              p={p}
              onFollow={toggleFollow}
              viewer={viewer}
              showMessageButton={mineTab === "friends"}
              onMessage={setChatPeer}
              onRemoved={(uid) => setRows((prev) => prev.filter((r) => r.id !== uid))}
            />
          ))}
        </div>
      )}
      {chatPeer && (
        <DMChatDrawer peer={chatPeer} onClose={() => setChatPeer(null)} />
      )}
    </div>
  );
}
